import { createContext, useCallback, useContext, useReducer, useMemo } from "react";
import PropTypes from "prop-types";
import { useQueryClient } from "@tanstack/react-query";
const gql = String.raw;

import {
	handleAddSubscription,
	handleAddSubscriptions,
	handleDeleteSubscription,
	handleClearSubscriptionContext,
	handleClearSubscriptions,
	handleGetSubscription,
} from "./subscriptionsHandlers";
import { ApolloProvider } from "@apollo/client";
import apolloClient from "@/clientApollo";

const SubscriptionsContext = createContext();

function handleSubscriptions(state, action) {
	switch (action.type) {
		case "add": {
			return handleAddSubscription(state, action);
		}
		case "addMany": {
			return handleAddSubscriptions(state, action);
		}
		case "delete": {
			return handleDeleteSubscription(state, action);
		}
		case "clearContext": {
			return handleClearSubscriptionContext(state);
		}
		case "clear": {
			return handleClearSubscriptions(state);
		}
		default: {
			throw new Error(`Unhandled action type: ${action.type}`);
		}
	}
}

function SubscriptionsProvider({ maxSubscriptions = 30, children }) {
	const queryClient = useQueryClient();
	const [subscriptions, handleSubscription] = useReducer(handleSubscriptions, { subscriptions: new Map() });

	const addSubscriptions = useCallback(
		(subscriptionsList) => handleSubscription({ type: "addMany", queryClient, subscriptionsList, maxSubscriptions }),
		[queryClient, maxSubscriptions]
	);

	const addSubscription = useCallback(
		(identifier, subscriptionsObj, persistent = false) =>
			handleSubscription({ queryClient, identifier, persistent, maxSubscriptions, type: "add", subscriptions: subscriptionsObj }),
		[queryClient, maxSubscriptions]
	);

	const deleteSubscription = useCallback((identifier) => handleSubscription({ type: "delete", identifier }), []);

	const clearSubscriptionContext = useCallback(() => handleSubscription({ type: "clearContext" }), []);

	const clearSubscriptions = useCallback(() => handleSubscription({ type: "clear" }), []);

	const getSubscription = useCallback((identifier) => handleGetSubscription(subscriptions, identifier), [subscriptions]);

	const store = useMemo(
		() => ({
			subscriptions,
			getSubscription,
			addSubscription,
			addSubscriptions,
			deleteSubscription,
			clearSubscriptionContext,
			clearSubscriptions,
		}),
		[
			subscriptions,
			getSubscription,
			addSubscription,
			addSubscriptions,
			deleteSubscription,
			clearSubscriptionContext,
			clearSubscriptions,
		]
	);
	if (import.meta.env.VITE_APP_AWS_APPSYNC_RTC === "http://localhost:4000/") {
		return (
			<ApolloProvider client={apolloClient}>
				<SubscriptionsContext.Provider value={store}>{children}</SubscriptionsContext.Provider>
			</ApolloProvider>
		);
	}
	return <SubscriptionsContext.Provider value={store}>{children}</SubscriptionsContext.Provider>;
}

SubscriptionsProvider.propTypes = {
	children: PropTypes.node,
	maxSubscriptions: PropTypes.number,
};

function useSubscriptions() {
	const context = useContext(SubscriptionsContext);
	if (context === undefined) {
		throw new Error("useSubscriptions must be used within a SubscriptionsProvider");
	}
	return context;
}

export { SubscriptionsProvider, useSubscriptions, gql };
